import { useEffect, useState } from 'react';
import type { SubmitRolloutResponse, ValidationIssue } from '../types';
import { Button, Card, Notice, Spinner, SummaryRow, cx } from '../components/ui/primitives';

/**
 * Processing, Success and Error screens.
 *
 * They share a single visual pattern — a centred card in the same design
 * language as the form — so they are kept together. Sign-in lives on the start
 * page instead, alongside the brief paste area.
 */

// ---------------------------------------------------------------------------
// Processing
// ---------------------------------------------------------------------------

const PROCESSING_STEPS = [
  'Validating brief',
  'Connecting to Google Drive',
  'Finding destination folder',
  'Checking existing documents',
  'Creating or updating document',
  'Saving rollout information',
];

/**
 * Progress display.
 *
 * The backend performs the work as one call, so the steps advance on a timer to
 * show progress rather than claiming completion. The final step stays active
 * until the real response arrives — it never shows all steps done prematurely.
 */
export function ProcessingScreen() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      // Hold on the last step; only a response ends this screen.
      setActive((current) => Math.min(current + 1, PROCESSING_STEPS.length - 1));
    }, 900);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="mx-auto max-w-md">
      <Card className="p-7">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Spinner className="h-5 w-5 text-accent" />
            <h2 className="text-heading font-semibold text-ink">Creating rollout information</h2>
          </div>

          <ol className="space-y-3">
            {PROCESSING_STEPS.map((step, index) => {
              const isDone = index < active;
              const isActive = index === active;

              return (
                <li key={step} className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className={cx(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                      isDone && 'bg-accent-soft text-accent',
                      isActive && 'bg-accent text-white',
                      !isDone && !isActive && 'bg-surface-muted text-ink-faint',
                    )}
                  >
                    {isDone ? '✓' : index + 1}
                  </span>
                  <span
                    className={cx(
                      'text-field',
                      isActive ? 'font-medium text-ink' : isDone ? 'text-ink-soft' : 'text-ink-faint',
                    )}
                  >
                    {step}
                  </span>
                </li>
              );
            })}
          </ol>

          <p className="text-micro text-ink-faint">
            Please keep this tab open. Closing it will not undo work already saved to Drive.
          </p>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success
// ---------------------------------------------------------------------------

export function SuccessScreen({
  result,
  onStartAnother,
}: {
  result: SubmitRolloutResponse;
  onStartAnother: () => void;
}) {
  const actionLabel =
    result.action === 'ADD_TAB'
      ? result.usedNativeTab
        ? 'Added a new tab to the existing document'
        : 'Added a new section to the existing document'
      : 'Created a new document';

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Card className="p-5 sm:p-7">
        <div className="space-y-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success-soft text-success"
            >
              <svg
                viewBox="0 0 20 20"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4.5 10.5 8 14l7.5-7.5" />
              </svg>
            </span>
            <div>
              <h2 className="text-heading font-semibold text-ink">
                Rollout Information Created Successfully
              </h2>
              <p className="mt-1 text-micro text-ink-muted">{actionLabel}.</p>
            </div>
          </div>

          <dl className="space-y-2.5">
            <SummaryRow label="Document Name">{result.documentName}</SummaryRow>
            <SummaryRow label="Brand">{result.brandName}</SummaryRow>
            {result.agencyName ? (
              <SummaryRow label="Agency">{result.agencyName}</SummaryRow>
            ) : null}
            <SummaryRow label="Channel">{result.channel}</SummaryRow>
            <SummaryRow label="Tab">{result.tabTitle}</SummaryRow>
            <SummaryRow label="Action Taken">{actionLabel}</SummaryRow>
            {result.folderPath ? (
              <SummaryRow label="Folder">{result.folderPath}</SummaryRow>
            ) : null}
          </dl>

          {/*
            A fallback section rather than a native tab is worth saying out loud,
            so nobody goes looking for a tab that is not there.
          */}
          {result.action === 'ADD_TAB' && !result.usedNativeTab ? (
            <Notice tone="warning" title="Added as a section, not a tab">
              Google would not accept a new tab on this document, so the rollout was appended as a
              clearly separated section instead. Nothing was overwritten.
            </Notice>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <Button variant="secondary" onClick={onStartAnother}>
              Create Another Brief
            </Button>
            <a
              href={result.documentUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center justify-center gap-2 rounded-button bg-primary px-6 py-2.5 text-field font-medium text-white transition-colors hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2"
            >
              Open Google Document
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

/**
 * Renders a message with any URLs turned into links.
 *
 * Some server messages carry a remedy the user has to visit — the Cloud Console
 * link for a disabled API, for instance. Leaving it as plain text would make the
 * user copy it out by hand.
 */
function Linkified({ text }: { text: string }) {
  // Trailing sentence punctuation must not be swallowed into the href.
  const parts = text.split(/(https?:\/\/[^\s]*[^\s.,;:)\]}])/g);

  return (
    <>
      {parts.map((part, index) =>
        /^https?:\/\//.test(part) ? (
          <a
            key={`link-${index}`}
            href={part}
            target="_blank"
            rel="noreferrer noopener"
            className="break-all font-medium text-accent underline underline-offset-2 hover:text-accent-hover"
          >
            {part}
          </a>
        ) : (
          part
        ),
      )}
    </>
  );
}

export function ErrorPanel({
  title,
  message,
  issues = [],
  onRetry,
  onBack,
  onConnect,
  needsGoogle = false,
}: {
  title: string;
  message: string;
  /**
   * Fields the server rejected. Listed here because a rejection can name a path
   * with no visible input behind it — `sources.cohorts`, say — and a message
   * with nothing to act on is worse than no message at all.
   */
  issues?: ValidationIssue[];
  onRetry?: () => void;
  onBack?: () => void;
  onConnect?: () => void;
  needsGoogle?: boolean;
}) {
  return (
    <Notice tone="error" title={title}>
      <p>
        <Linkified text={message} />
      </p>

      {issues.length ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-4">
          {issues.map((issue) => (
            <li key={`${issue.path}-${issue.message}`}>
              <span className="font-mono text-micro text-ink">{issue.path || 'request'}</span>
              {' — '}
              {issue.message}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-3">
        {needsGoogle && onConnect ? (
          <Button variant="secondary" onClick={onConnect} className="px-4 py-1.5">
            Reconnect Google Drive
          </Button>
        ) : null}
        {onRetry ? (
          <Button variant="secondary" onClick={onRetry} className="px-4 py-1.5">
            Try again
          </Button>
        ) : null}
        {onBack ? (
          <Button variant="ghost" onClick={onBack}>
            Go back
          </Button>
        ) : null}
      </div>
    </Notice>
  );
}
